import {
  HeartHandshake, Users, Utensils, Pill, PersonStanding, Car, Moon, Brain, Activity,
} from "lucide-react";
import { Link } from "react-router-dom";
import LegalNotice from "@/components/LegalNotice";

const SERVICES = [
  { icon: HeartHandshake, title: "Personal Care Assistance", desc: "Dignified help with bathing, grooming, toileting, dressing and daily hygiene.", benefits: ["Maintains dignity", "Fall prevention", "Skin & hygiene support"] },
  { icon: Users, title: "Companion Care", desc: "Meaningful social engagement, conversation, hobbies, reading and friendship.", benefits: ["Fights isolation", "Improves mood", "Family peace of mind"] },
  { icon: Utensils, title: "Meal Preparation", desc: "Balanced, delicious meals based on dietary needs and personal preferences.", benefits: ["Better nutrition", "Shopping included", "Diet-specific menus"] },
  { icon: Pill, title: "Medication Reminders", desc: "Timely reminders so prescriptions are taken correctly every day.", benefits: ["Fewer missed doses", "Family alerts", "Safer adherence"] },
  { icon: PersonStanding, title: "Mobility Support", desc: "Safe transfers, walking assistance and fall-prevention strategies at home.", benefits: ["Safer movement", "Transfer assistance", "Fall-risk reduction"] },
  { icon: Car, title: "Transportation Assistance", desc: "Dependable rides to doctor's appointments, errands and social outings.", benefits: ["Door-to-door support", "Companion rides", "Stay independent"] },
  { icon: Moon, title: "Respite Care", desc: "Short-term relief for family caregivers — rest without worry.", benefits: ["Family wellbeing", "Flexible hours", "Trained substitutes"] },
  { icon: Brain, title: "Alzheimer's & Dementia Care", desc: "Specialized memory care focused on routine, safety and gentle connection.", benefits: ["Trained in memory care", "Structured routines", "Reduced agitation"] },
  { icon: Activity, title: "Post-Hospital Recovery", desc: "Smooth transition home after surgery or hospital stay, reducing readmission risk.", benefits: ["Follow-up reminders", "Mobility rebuild", "Lower readmission risk"] },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="section bg-gradient-to-b from-slate-50 to-white">
        <div className="container-x max-w-4xl">
          <div className="eyebrow">Our services</div>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            Thoughtful support <span className="brand-gradient-text">for every stage of life at home.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Every service below can be combined into a custom care plan. Tell us what's needed — we'll build the right schedule and caregiver fit.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map(({ icon: Icon, title, desc, benefits }) => (
            <div key={title} className="soft-card flex flex-col" data-testid={`service-detail-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="w-12 h-12 rounded-xl brand-gradient flex items-center justify-center">
                <Icon className="w-6 h-6 text-white" strokeWidth={1.75} />
              </div>
              <h3 className="mt-5 text-xl font-heading font-semibold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
              <ul className="mt-4 space-y-1.5">
                {benefits.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-slate-700">
                    <span className="heart-dot" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="container-x mt-10">
          <LegalNotice variant="availability" testId="services-availability-notice">
            Every service is offered under a personalized care plan and is subject to caregiver availability,
            licensing requirements and geographic coverage. Care plans may be adjusted as circumstances change.
            See Terms §2 and §14 for details.
          </LegalNotice>
        </div>
      </section>

      <section className="section brand-gradient text-white">
        <div className="container-x text-center max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-semibold text-white">Unsure which service fits?</h2>
          <p className="mt-4 text-white/90">Schedule a free 20-minute consultation. We'll listen, assess and recommend a plan — no pressure.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/consultation" className="bg-white text-brand-navy rounded-full px-6 py-3 font-semibold hover:-translate-y-0.5 transition" data-testid="services-consultation-cta">Book free consultation</Link>
            <Link to="/request-care" className="rounded-full border-2 border-white/70 text-white px-6 py-3 font-semibold hover:bg-white hover:text-brand-navy transition" data-testid="services-request-care-cta">Request care</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
